import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Settings, Archive as ArchiveIcon, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Info, PanelRightClose, PanelRightOpen, Printer } from "lucide-react";
import { PlannerCalendar, navigateDate, formatRangeLabel, toISODate } from "@/components/tour-planner/PlannerCalendar";
import { ScheduleTourModal } from "@/components/tour-planner/ScheduleTourModal";
import { PlannerSettingsDrawer } from "@/components/tour-planner/PlannerSettingsDrawer";
import { ArchiveDrawer } from "@/components/tour-planner/ArchiveDrawer";
import { LiveRatiosPanel } from "@/components/tour-planner/LiveRatiosPanel";
import { TransitionsPanel } from "@/components/tour-planner/TransitionsPanel";
import { ProjectedRatiosPanel } from "@/components/tour-planner/ProjectedRatiosPanel";
import { plannerApi } from "@/components/tour-planner/plannerApi";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/layout/AppShell";
import FeatureGate from "@/components/FeatureGate";
import PrintReportHeader from "@/components/common/PrintReportHeader";

const defaultProjectedDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return toISODate(d);
};

const HERO_IMAGE_URL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_5dece0cf-00d5-4dde-adb2-b220964f6178/artifacts/856pj34t_image.png";
const DEFAULT_HERO_IMAGE = "https://customer-assets-m6fa6gv7.emergentagent.net/job_5dece0cf-00d5-4dde-adb2-b220964f6178/artifacts/n3jwvpal_tour_ratio_hero_reference.png";

export default function TourRatioPlannerPage() {
  const { user } = useAuth();
  const isDirector = user?.role === "owner" || user?.role?.toLowerCase() === "co-owner";
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tz, setTz] = useState("America/New_York");
  const [kgCutoffDate, setKgCutoffDate] = useState("09-01");
  const [rooms, setRooms] = useState([]);
  const [tours, setTours] = useState([]);
  const [withdrawalPlans, setWithdrawalPlans] = useState([]);
  const [view, setView] = useState("month");
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [liveRatios, setLiveRatios] = useState({ rooms: [], categoryGroups: [], unassignedChildren: [], staffMissingRoomAssignment: [], sharedPools: [] });
  const [transitions, setTransitions] = useState([]);
  const [projectedDate, setProjectedDate] = useState(defaultProjectedDate());
  const [projectedRatios, setProjectedRatios] = useState({ rooms: [], sharedPools: [] });
  const [transitionsSidebarOpen, setTransitionsSidebarOpen] = useState(true);
  const [heroImages, setHeroImages] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroOpacity, setHeroOpacity] = useState(1);

  const loadHeroImages = useCallback(async () => {
    try {
      const res = await plannerApi.listHeroImages();
      const metas = res.data || [];
      const withUrls = await Promise.all(metas.map(async (m) => {
        try {
          const blobRes = await plannerApi.getHeroImageBlob(m.id);
          return { ...m, url: URL.createObjectURL(blobRes.data) };
        } catch {
          return null;
        }
      }));
      setHeroImages(withUrls.filter(Boolean));
      setHeroIndex(0);
    } catch {
      setHeroImages([]);
    }
  }, []);

  useEffect(() => { loadHeroImages(); }, [loadHeroImages]);

  useEffect(() => {
    return () => { heroImages.forEach((im) => im.url && URL.revokeObjectURL(im.url)); };
  }, [heroImages]);

  useEffect(() => {
    if (heroImages.length < 2) return;
    const t = setInterval(() => {
      setHeroOpacity(0);
      setTimeout(() => {
        setHeroIndex((i) => (i + 1) % heroImages.length);
        setHeroOpacity(1);
      }, 500);
    }, 6000);
    return () => clearInterval(t);
  }, [heroImages]);

  const loadRatios = useCallback(async () => {
    try {
      const [live, trans] = await Promise.all([plannerApi.getLiveRatios(), plannerApi.getTransitions()]);
      setLiveRatios(live.data || { rooms: [], unassignedChildren: [] });
      setTransitions(trans.data || []);
    } catch {
      /* Live Ratios / Transitions panels simply stay empty if this call fails — the rest of the page still works. */
    }
  }, []);

  const loadProjected = useCallback(async () => {
    try {
      const res = await plannerApi.getProjectedRatios(projectedDate);
      setProjectedRatios(res.data || { rooms: [] });
    } catch {
      setProjectedRatios({ rooms: [] });
    }
  }, [projectedDate]);

  const load = useCallback(async () => {
    try {
      const [s, r, t, wp] = await Promise.all([
        plannerApi.getSettings(),
        plannerApi.listRooms(),
        plannerApi.listTours(),
        plannerApi.listWithdrawalPlans(),
      ]);
      setTz(s.data.timezone || "America/New_York");
      setKgCutoffDate(s.data.kindergartenCutoffDate || "09-01");
      setRooms(r.data || []);
      setTours(t.data || []);
      setWithdrawalPlans(wp.data || []);
      setDenied(false);
      loadRatios();
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error("Failed to load planner");
    } finally {
      setLoading(false);
    }
  }, [loadRatios]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProjected(); }, [loadProjected]);

  const roomsById = Object.fromEntries(rooms.map((r) => [r.id, r]));
  const todaysTourCount = tours.filter((t) => t.date === toISODate(new Date())).length;
  const roomsUnderRatioCount = liveRatios.rooms.filter((r) => r.isUnderRatio).length;
  const activeHero = heroImages[heroIndex];
  const heroBgUrl = activeHero ? activeHero.url : HERO_IMAGE_URL;
  const heroBgSize = activeHero ? (activeHero.fit === "stretch" ? "100% 100%" : activeHero.fit) : "cover";
  const heroBgPos = activeHero ? `${activeHero.hPos} ${activeHero.vPos}` : "center 18%";

  const openNewTour = () => { setEditingTour(null); setModalOpen(true); };
  const openTour = (tour) => { setEditingTour(tour); setModalOpen(true); };
  const closeModal = () => setModalOpen(false);
  const onSaved = () => { setModalOpen(false); load(); };
  const onCancelled = () => { setModalOpen(false); load(); };

  if (denied) {
    return (
      <AppShell pageTitle="Tour &amp; Ratio Planner" breadcrumb>
        <div className="p-8 text-slate-500 bg-slate-50 min-h-[60vh] flex items-center justify-center" data-testid="planner-access-denied">
          You do not have access to the Tour &amp; Ratio Planner.
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell pageTitle="Tour & Ratio Planner" breadcrumb>
    <FeatureGate feature="tour_ratio_planner">
    <div className="min-h-screen bg-[#F6F7F8] p-6" data-testid="tour-planner-page">
      <PrintReportHeader />
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="print:hidden" data-testid="planner-hero-banner">
          {!activeHero && isDirector && (
            <button
              onClick={() => setSettingsOpen(true)}
              className="flex items-center gap-1.5 text-xs text-[#4C8C8C] hover:text-[#3f7575] font-medium mb-2"
              data-testid="customize-hero-photos-hint"
            >
              <Settings className="w-3.5 h-3.5" /> Customize with your center's photos in Settings →
            </button>
          )}
          {activeHero ? (
            <div
              className="relative rounded-2xl overflow-hidden h-[320px] sm:h-[380px] lg:h-[420px] bg-slate-800"
              style={{
                backgroundImage: `url(${heroBgUrl})`,
                backgroundSize: heroBgSize,
                backgroundPosition: heroBgPos,
                opacity: heroOpacity,
                transition: "opacity 0.5s ease",
              }}
              data-testid="planner-hero-photo"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/10" />
              <div className="relative z-10 h-full flex flex-col justify-center px-6 py-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-white">Tour &amp; Ratio Planner</h1>
                <p className="text-sm text-white/80 mt-1">Schedule tours, manage rooms, and set reminders.</p>
              </div>
            </div>
          ) : (
            <img
              src={DEFAULT_HERO_IMAGE}
              alt="Tour & Ratio Planner"
              className="w-full h-auto rounded-2xl block"
              data-testid="planner-hero-default"
            />
          )}
          <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
            <div className="flex gap-2 flex-wrap" data-testid="planner-hero-summary-pills">
              <span className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-xs text-slate-600" data-testid="pill-tours-today">
                <span className="w-2 h-2 rounded-full bg-[#4C8C8C]" /> {todaysTourCount} tour{todaysTourCount === 1 ? "" : "s"} today
              </span>
              <span className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-xs text-slate-600" data-testid="pill-rooms-under-ratio">
                <span className="w-2 h-2 rounded-full bg-[#D8A657]" /> {roomsUnderRatioCount} room{roomsUnderRatioCount === 1 ? "" : "s"} under ratio
              </span>
              <span className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-xs text-slate-600" data-testid="pill-age-transitions">
                <span className="w-2 h-2 rounded-full bg-[#4C8C8C]" /> {transitions.length} age transition{transitions.length === 1 ? "" : "s"} due soon
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex bg-slate-100 rounded-full p-0.5">
                <button
                  onClick={() => setView("week")}
                  data-testid="planner-view-week"
                  className={`text-xs px-3 py-1 rounded-full transition-colors ${view === "week" ? "bg-[#4C8C8C] text-white" : "text-slate-500"}`}
                >
                  Week
                </button>
                <button
                  onClick={() => setView("month")}
                  data-testid="planner-view-month"
                  className={`text-xs px-3 py-1 rounded-full transition-colors ${view === "month" ? "bg-[#4C8C8C] text-white" : "text-slate-500"}`}
                >
                  Month
                </button>
              </div>
              <Button variant="ghost" size="icon" className="text-slate-500 h-8 w-8" onClick={() => window.print()} data-testid="print-calendar-btn" title="Print Calendar">
                <Printer className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="text-slate-500 h-8 w-8" onClick={() => setArchiveOpen(true)} data-testid="open-archive-btn" title="Archive">
                <ArchiveIcon className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="text-slate-500 h-8 w-8" onClick={() => setSettingsOpen(true)} data-testid="open-settings-btn" title="Planner Settings">
                <Settings className="w-4 h-4" />
              </Button>
              <Button className="bg-[#4C8C8C] hover:bg-[#3f7575] text-white" onClick={openNewTour} data-testid="open-schedule-tour-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Schedule Tour
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm print:hidden">
          <button
            onClick={() => setShowHelp((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
            data-testid="planner-help-toggle"
          >
            <div className="flex items-center gap-2">
              <Info className="w-5 h-5 text-[#4C8C8C]" />
              <span className="font-semibold text-slate-700">Set Up Your Planner Before Planning Tours</span>
            </div>
            {showHelp ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>

          {showHelp && (
            <div className="px-5 pb-5 space-y-3 border-t border-slate-100" data-testid="planner-help-content">
              <ul className="text-sm text-slate-600 list-disc pl-5 space-y-2 mt-4">
                <li>Open Planner Settings and enter each classroom's approved capacity and normal staff-to-child ratio.</li>
                <li>The planner uses the app's existing enrolled child Categories to determine how many seats are occupied.</li>
                <li>Staff counts come from each staff member's Assigned Category, set in Edit Staff.</li>
                <li>Tours are hypothetical only and never reserve a seat or change enrollment.</li>
                <li>Live and Projected Ratios depend on the room capacity and ratio information entered in Planner Settings.</li>
                <li>Birthdate is used only for age comparison and future transition assistance. A missing birthdate does not remove a child from counts.</li>
              </ul>
              <Button
                size="sm"
                className="bg-[#4C8C8C] hover:bg-[#3f7575] text-white"
                onClick={() => setSettingsOpen(true)}
                data-testid="planner-help-open-settings-btn"
              >
                Open Planner Settings
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 print:hidden">
          <div className="flex items-center gap-1">
            <button onClick={() => setAnchorDate(navigateDate(anchorDate, view, -1))} data-testid="planner-nav-prev" className="p-1.5 rounded hover:bg-slate-200">
              <ChevronLeft className="w-4 h-4 text-slate-500" />
            </button>
            <span className="text-sm font-medium text-slate-600 min-w-[160px] text-center" data-testid="planner-range-label">
              {formatRangeLabel(anchorDate, view)}
            </span>
            <button onClick={() => setAnchorDate(navigateDate(anchorDate, view, 1))} data-testid="planner-nav-next" className="p-1.5 rounded hover:bg-slate-200">
              <ChevronRight className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          <button
            onClick={() => setTransitionsSidebarOpen((o) => !o)}
            data-testid="transitions-sidebar-toggle"
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-100"
          >
            {transitionsSidebarOpen ? <PanelRightClose className="w-3.5 h-3.5" /> : <PanelRightOpen className="w-3.5 h-3.5" />}
            {transitionsSidebarOpen ? "Hide Age Transitions" : "Show Age Transitions"}
          </button>
        </div>

        <div className={`grid grid-cols-1 gap-4 items-start print:grid-cols-1 print-calendar-row ${transitionsSidebarOpen ? "lg:grid-cols-[minmax(0,1fr)_320px]" : ""}`}>
          <div className="bg-white rounded-xl border border-slate-200 p-4 print-calendar-card">
            {loading ? (
              <p className="text-sm text-slate-400 py-10 text-center">Loading planner…</p>
            ) : (
              <PlannerCalendar
                view={view}
                anchorDate={anchorDate}
                tours={tours}
                withdrawals={withdrawalPlans}
                roomsById={roomsById}
                onSelectTour={openTour}
                onSelectWithdrawal={() => setSettingsOpen(true)}
              />
            )}
          </div>
          {transitionsSidebarOpen && <div className="print-page2"><TransitionsPanel transitions={transitions} /></div>}
        </div>

        <div className="print-page2">
          <LiveRatiosPanel categoryGroups={liveRatios.categoryGroups} unassignedChildren={liveRatios.unassignedChildren} staffMissingRoomAssignment={liveRatios.staffMissingRoomAssignment} sharedPools={liveRatios.sharedPools} />
        </div>
        <div className="print-page2">
          <ProjectedRatiosPanel
            date={projectedDate}
            onDateChange={setProjectedDate}
          rooms={projectedRatios.rooms}
          sharedPools={projectedRatios.sharedPools}
        />
      </div>
      </div>

      <ScheduleTourModal
        open={modalOpen}
        onClose={closeModal}
        rooms={rooms}
        tour={editingTour}
        defaultDate={toISODate(anchorDate)}
        onSaved={onSaved}
        onCancelled={onCancelled}
      />
      <PlannerSettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} tz={tz} kgCutoffDate={kgCutoffDate} rooms={rooms} onSaved={load} isDirector={isDirector} onHeroImagesChanged={loadHeroImages} />
      <ArchiveDrawer open={archiveOpen} onClose={() => setArchiveOpen(false)} onChanged={load} />
    </div>
    </FeatureGate>
    </AppShell>
  );
}
